// ENDORSE-CANCEL-F — the console policy-detail read + the endorsement/cancellation write
// shapes, verified at source. GET /v1/policies/:id returns the FLAT PolicyRow — there is no
// endorsements list and no separate cancellation record; cancellation metadata is inline.

export type PolicyStatus =
  | "ACTIVE"
  | "CANCELLATION_REQUESTED"
  | "CANCELLED"
  | "RENEWED"
  | "EXPIRED";

/** The subset of the policy row this surface reads/renders. */
export interface PolicyDetail {
  id: string;
  householdId: string;
  lineCode: string;
  carrierId: string;
  carrierPolicyNumber: string | null;
  termStart: string;
  termEnd: string;
  status: PolicyStatus;
  // Cancellation metadata is flat on the row (no sub-record).
  cancellationSubtype: string | null;
  cancellationReason: string | null;
  cancellationRequestedAt: string | null;
  cancelledAt: string | null;
  proRataReturnPremium: string | null;
}

export const CANCELLATION_SUBTYPES = ["CUSTOMER", "CARRIER_NONPAYMENT", "CSA"] as const;
export type CancellationSubtype = (typeof CANCELLATION_SUBTYPES)[number];

// Endorsement — this surface drives the NON_PREMIUM_AFFECTING request → effect loop only.
// PREMIUM_AFFECTING needs an accepted offer and is effected at the issuance desk (F2), so it
// is out of scope here (the /effect endpoint is non-premium only).
export interface RequestEndorsementInput {
  kind: "NON_PREMIUM_AFFECTING";
  requestedChange: Record<string, unknown>;
}
export interface EndorsementResult {
  endorsementId: string;
  status: string;
  applicationId?: string;
}
export interface EffectEndorsementResult {
  endorsementId: string;
  resultingVersionId: string;
}

// A2 — GET /v1/policies/:id/endorsements returns a BARE ARRAY of endorsement rows
// (sibling of households/:id/policies, which is also bare). newest first.
// Mirrors ENDORSEMENT_STATUSES (packages/db/src/schema/policy.ts:43).
export type EndorsementStatus =
  | "ENDORSEMENT_REQUESTED"
  | "ADDITIONAL_PREMIUM_PENDING"
  | "RETURN_PREMIUM_PENDING"
  | "ENDORSEMENT_EFFECTED"
  | "ENDORSEMENT_REJECTED"
  | "WITHDRAWN";
export interface EndorsementListRow {
  id: string;
  policyId: string;
  kind: string;
  status: EndorsementStatus;
  requestedChange: Record<string, unknown>;
  applicationId: string | null;
  resultingVersionId: string | null;
  premiumDelta: string | null;
  createdAt: string;
}

export interface RequestCancellationInput {
  subtype: CancellationSubtype;
  reason: string;
}
export interface ConfirmCancellationInput {
  /** Carrier-reported pro-rata return premium (absent when none is returned). */
  proRataReturnPremium?: string;
}
